import i18n from 'i18next';
import { StateCreator } from 'zustand';

import { ApplicationUpdateMessage } from './enums/ApplicationUpdateMessage';
import { isValidApplicationUpdateMessage } from './functions/isValidApplicationUpdateMessage';
import { ApplicationUpdateState } from './types/ApplicationUpdateState';
import { ApplicationUpdateStateDependencies } from './types/ApplicationUpdateStateDependencies';

export const keysToPersist = ['viewedOffers'];

export const applicationUpdateStore =
  ({
    useApiStore,
    useLeadChannelStore,
    useParameterStore,
  }: ApplicationUpdateStateDependencies): StateCreator<ApplicationUpdateState> =>
  (set, get) => ({
    // viewedOffers is used to track all of the applications for which a user has viewed an updated offer.
    // it is an array of autopayNumbers.
    viewedOffers: [],

    computed: {
      get displayOfferInformation(): boolean {
        const { applicationUpdateMessage, amount, apr, term } =
          useParameterStore.getState();

        const offerMessages: string[] = [
          ApplicationUpdateMessage.TERMS_UPDATED,
          ApplicationUpdateMessage.HAS_APPROVAL,
        ];

        const isOfferMessage = offerMessages.includes(applicationUpdateMessage);

        const hasOfferDetails = [amount, apr, term].every(Boolean);

        return isOfferMessage && hasOfferDetails;
      },
      get shouldReportViewedOffer(): boolean {
        const { autopayNumber } = useParameterStore.getState();
        const {
          computed: { displayOfferInformation },
          viewedOffers,
        } = get();

        return (
          Boolean(autopayNumber) && // autopayNumber query parameter is provided
          displayOfferInformation && // Offer information is displayed
          !viewedOffers.includes(autopayNumber) // Offer has not been viewed for this autopayNumber
        );
      },
      get subtitleText(): string {
        const { applicationUpdateMessage } = useParameterStore.getState();

        return isValidApplicationUpdateMessage(applicationUpdateMessage)
          ? i18n.t(`ApplicationUpdatePage.subtitle.${applicationUpdateMessage}`)
          : i18n.t('ApplicationUpdatePage.subtitle.default');
      },
      get titleText(): string {
        const {
          computed: { displayLabel },
        } = useLeadChannelStore.getState();

        return displayLabel
          ? i18n.t('ApplicationUpdatePage.title.withDisplayLabel', {
              displayLabel,
            })
          : i18n.t('ApplicationUpdatePage.title.default');
      },
    },

    reportViewedOffer: (): Promise<void> => {
      const { setViewedOffer } = get();
      const { autopayNumber } = useParameterStore.getState();

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest
        .post('/2.0/notes/viewed-offer', {
          autopayNumber,
        })
        .then((): void => {
          setViewedOffer(autopayNumber);
        });
    },
    setViewedOffer: (autopayNumber: string): void => {
      set((state) => ({
        viewedOffers: [...state.viewedOffers, autopayNumber],
      }));
    },
  });
